import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, EyeOff } from "lucide-react";

export const emptyCandidate = {
  full_name: "", email: "", phone: "", headline: "", location: "", linkedin_url: "",
  summary: "", industries: "", functions: "", stage_experience: "", compensation_expectations: "",
  board_interest: false, fractional_interest: false, advisor_interest: false,
  availability_status: "open", geographic_preferences: "", tags: "", notes_internal: "",
  visibility_status: "searchable",
};

// Turns a candidate record (arrays) into the flat comma-string form shape.
export const candidateToForm = (c) => ({
  full_name: c.full_name || "", email: c.email || "", phone: c.phone || "",
  headline: c.headline || "", location: c.location || "", linkedin_url: c.linkedin_url || "",
  summary: c.summary || "", compensation_expectations: c.compensation_expectations || "",
  industries: (c.industries || []).join(", "), functions: (c.functions || []).join(", "),
  stage_experience: (c.stage_experience || []).join(", "),
  geographic_preferences: (c.geographic_preferences || []).join(", "),
  tags: (c.tags || []).join(", "), notes_internal: c.notes_internal || "",
  board_interest: !!c.board_interest, fractional_interest: !!c.fractional_interest,
  advisor_interest: !!c.advisor_interest, availability_status: c.availability_status || "open",
  visibility_status: c.visibility_status === "confidential" ? "confidential" : "searchable",
});

// Converts the flat form back into the API payload (arrays).
export const formToCandidatePayload = (form) => {
  const toList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
  return {
    ...form,
    industries: toList(form.industries),
    functions: toList(form.functions),
    stage_experience: toList(form.stage_experience),
    geographic_preferences: toList(form.geographic_preferences),
    tags: toList(form.tags),
  };
};

export function CandidateFormFields({ form, setForm, meta }) {
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const input = "bg-white border-black/10 rounded-none";
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Full name</Label><Input data-testid="cand-name-input" value={form.full_name} onChange={upd("full_name")} required className={input} /></div>
        <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={upd("email")} className={input} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={upd("phone")} className={input} /></div>
        <div className="space-y-2"><Label>Location</Label><Input value={form.location} onChange={upd("location")} className={input} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Headline</Label><Input value={form.headline} onChange={upd("headline")} className={input} placeholder="Ex-CTO, Series B fintech" /></div>
        <div className="space-y-2">
          <Label>Availability</Label>
          <Select value={form.availability_status} onValueChange={(v) => setForm({ ...form, availability_status: v })}>
            <SelectTrigger data-testid="cand-availability" className={input}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#F4F4F5] border-black/10">
              {meta.availability.map((a) => <SelectItem key={a} value={a}>{a.replace(/_/g, " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>LinkedIn URL</Label><Input value={form.linkedin_url} onChange={upd("linkedin_url")} className={input} placeholder="https://linkedin.com/in/" /></div>
        <div className="space-y-2"><Label>Compensation expectations</Label><Input value={form.compensation_expectations} onChange={upd("compensation_expectations")} className={input} placeholder="$250k+ / equity" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Industries (comma sep)</Label><Input value={form.industries} onChange={upd("industries")} className={input} placeholder="fintech, ai" /></div>
        <div className="space-y-2"><Label>Functions (comma sep)</Label><Input value={form.functions} onChange={upd("functions")} className={input} placeholder="engineering, product" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2"><Label>Stage experience</Label><Input value={form.stage_experience} onChange={upd("stage_experience")} className={input} placeholder="seed, series a" /></div>
        <div className="space-y-2"><Label>Geographic preferences</Label><Input value={form.geographic_preferences} onChange={upd("geographic_preferences")} className={input} placeholder="remote, nyc" /></div>
      </div>
      <div className="space-y-2"><Label>Tags</Label><Input value={form.tags} onChange={upd("tags")} className={input} placeholder="hot, referral" /></div>
      <div className="space-y-2"><Label>Summary</Label><Textarea value={form.summary} onChange={upd("summary")} className={`${input} min-h-[70px]`} /></div>
      <div className="space-y-2"><Label>Internal notes</Label><Textarea value={form.notes_internal} onChange={upd("notes_internal")} className={`${input} min-h-[60px]`} placeholder="Private to the recruiting team" /></div>
      <div className="space-y-2">
        <Label>Profile visibility</Label>
        <div className="grid grid-cols-2 gap-px bg-black/10 border border-black/10">
          <button type="button" data-testid="cand-vis-searchable" onClick={() => setForm({ ...form, visibility_status: "searchable" })}
            className={`p-3 text-left transition-colors ${form.visibility_status !== "confidential" ? "bg-[#09090B] text-white" : "bg-white hover:bg-black/5"}`}>
            <span className="flex items-center gap-2 font-heading font-bold text-sm"><Eye className="h-4 w-4" /> Searchable</span>
            <span className={`text-xs mt-1 block ${form.visibility_status !== "confidential" ? "text-white/60" : "text-muted-foreground"}`}>Discoverable and shareable with clients.</span>
          </button>
          <button type="button" data-testid="cand-vis-confidential" onClick={() => setForm({ ...form, visibility_status: "confidential" })}
            className={`p-3 text-left transition-colors ${form.visibility_status === "confidential" ? "bg-[#09090B] text-white" : "bg-white hover:bg-black/5"}`}>
            <span className="flex items-center gap-2 font-heading font-bold text-sm"><EyeOff className="h-4 w-4" /> Confidential</span>
            <span className={`text-xs mt-1 block ${form.visibility_status === "confidential" ? "text-white/60" : "text-muted-foreground"}`}>Recruiters are warned before sharing with a client.</span>
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-6 py-2">
        {[["board_interest", "Board"], ["fractional_interest", "Fractional"], ["advisor_interest", "Advisor"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-sm">
            <Switch checked={form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: v })} /> {l} interest
          </label>
        ))}
      </div>
    </>
  );
}
